/**
 * TasksPage — list view over vault_tasks scoped to this Holder.
 * Filter by status / priority / regarding. Inline complete / reopen /
 * cancel / soft-delete actions. Create new task modal.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Check, RotateCcw, XCircle, Trash2 } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsTasks,
  useCreateFiasLeadsTask,
  useCompleteFiasLeadsTask,
  useReopenFiasLeadsTask,
  useCancelFiasLeadsTask,
  useDeleteFiasLeadsTask,
  useFiasLeadsContacts,
  useFiasLeadsCompanies,
} from '../hooks/useFiasLeads';
import type {
  FiasLeadsTaskStatus,
  FiasLeadsTaskPriority,
  FiasLeadsTaskRegardingType,
} from '@/lib/api';
import { SkeletonRow } from '../components/Skeletons';

interface Props {
  holderId: string;
}

const PRIORITY_COLORS: Record<FiasLeadsTaskPriority, string> = {
  low: 'bg-slate-100 text-slate-700',
  normal: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-rose-100 text-rose-700',
};

const STATUS_COLORS: Record<FiasLeadsTaskStatus, string> = {
  open: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-200 text-slate-600',
};

export function TasksPage({ holderId }: Props) {
  const [statusFilter, setStatusFilter] = useState<FiasLeadsTaskStatus | ''>('open');
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<{
    title: string;
    priority: FiasLeadsTaskPriority;
    dueAt: string;
    regardingType: FiasLeadsTaskRegardingType | '';
    regardingId: string;
  }>({
    title: '',
    priority: 'normal',
    dueAt: '',
    regardingType: '',
    regardingId: '',
  });

  const tasksQuery = useFiasLeadsTasks(holderId, {
    status: statusFilter || undefined,
  });
  const createTask = useCreateFiasLeadsTask(holderId);
  const completeTask = useCompleteFiasLeadsTask(holderId);
  const reopenTask = useReopenFiasLeadsTask(holderId);
  const cancelTask = useCancelFiasLeadsTask(holderId);
  const deleteTask = useDeleteFiasLeadsTask(holderId);
  const contactsQuery = useFiasLeadsContacts(holderId);
  const companiesQuery = useFiasLeadsCompanies(holderId);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.title.trim()) return;
    await createTask.mutateAsync({
      title: draft.title.trim(),
      priority: draft.priority,
      dueAt: draft.dueAt || null,
      regardingType: draft.regardingType || undefined,
      regardingId: draft.regardingId || undefined,
    });
    setDraft({ title: '', priority: 'normal', dueAt: '', regardingType: '', regardingId: '' });
    setShowCreate(false);
  };

  const tasks = tasksQuery.data ?? [];

  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      // open first, then by due_at ASC, then updated_at DESC
      if (a.status === 'open' && b.status !== 'open') return -1;
      if (a.status !== 'open' && b.status === 'open') return 1;
      const aDue = a.dueAt ?? '9999-12-31';
      const bDue = b.dueAt ?? '9999-12-31';
      if (aDue !== bDue) return aDue < bDue ? -1 : 1;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [tasks]);

  const contactNameById = new Map(
    (contactsQuery.data ?? []).map((c) => [
      c.contactId,
      `${c.firstName} ${c.lastName ?? ''}`.trim(),
    ]),
  );
  const companyNameById = new Map((companiesQuery.data ?? []).map((c) => [c.companyId, c.name]));

  function regardingLabel(t: (typeof tasks)[number]) {
    if (!t.regardingType || !t.regardingId) return null;
    if (t.regardingType === 'vault_contact') {
      const name = contactNameById.get(t.regardingId) ?? t.regardingId;
      return (
        <Link
          to={`${ARCHE_PATH}/contacts/${t.regardingId}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          {name}
        </Link>
      );
    }
    if (t.regardingType === 'vault_company') {
      const name = companyNameById.get(t.regardingId) ?? t.regardingId;
      return (
        <Link
          to={`${ARCHE_PATH}/companies/${t.regardingId}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          {name}
        </Link>
      );
    }
    if (t.regardingType === 'fias_leads_deal') {
      return (
        <Link
          to={`${ARCHE_PATH}/deals/${t.regardingId}`}
          className="text-xs text-muted-foreground hover:underline"
        >
          deal {t.regardingId.slice(0, 8)}…
        </Link>
      );
    }
    return null;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Tasks</h1>
          <p className="text-sm text-muted-foreground">
            Sales follow-up tasks. Visible to Fias 360 on the same Holder.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FiasLeadsTaskStatus | '')}
            className="rounded border border-border bg-card px-3 py-2 text-sm"
          >
            <option value="open">Open</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="">All</option>
          </select>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
          >
            <Plus className="h-4 w-4" />
            New task
          </button>
        </div>
      </header>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="rounded border border-border bg-card p-4 flex flex-col gap-2"
        >
          <input
            type="text"
            placeholder="Task title *"
            value={draft.title}
            onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className="rounded border border-border bg-background px-3 py-2 text-sm"
            required
            autoFocus
          />
          <div className="flex flex-wrap gap-2">
            <select
              value={draft.priority}
              onChange={(e) =>
                setDraft((d) => ({ ...d, priority: e.target.value as FiasLeadsTaskPriority }))
              }
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            <input
              type="date"
              value={draft.dueAt}
              onChange={(e) => setDraft((d) => ({ ...d, dueAt: e.target.value }))}
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            />
            <select
              value={draft.regardingType}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  regardingType: e.target.value as FiasLeadsTaskRegardingType | '',
                  regardingId: '',
                }))
              }
              className="rounded border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">No parent</option>
              <option value="vault_contact">Contact</option>
              <option value="vault_company">Company</option>
              <option value="fias_leads_deal">Deal</option>
            </select>
            {draft.regardingType === 'vault_contact' && (
              <select
                value={draft.regardingId}
                onChange={(e) => setDraft((d) => ({ ...d, regardingId: e.target.value }))}
                className="rounded border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Select contact…</option>
                {(contactsQuery.data ?? []).map((c) => (
                  <option key={c.contactId} value={c.contactId}>
                    {c.firstName} {c.lastName ?? ''}
                  </option>
                ))}
              </select>
            )}
            {draft.regardingType === 'vault_company' && (
              <select
                value={draft.regardingId}
                onChange={(e) => setDraft((d) => ({ ...d, regardingId: e.target.value }))}
                className="rounded border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Select company…</option>
                {(companiesQuery.data ?? []).map((c) => (
                  <option key={c.companyId} value={c.companyId}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!draft.title.trim() || createTask.isPending}
              className="rounded bg-primary px-3 py-1.5 text-primary-foreground text-sm disabled:opacity-50"
            >
              {createTask.isPending ? 'Creating…' : 'Create task'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-3 py-1.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {tasksQuery.isLoading && <SkeletonRow />}

      <div className="rounded border border-border bg-card">
        <ul className="divide-y divide-border">
          {sortedTasks.map((t) => (
            <li key={t.taskId} className="p-3 flex items-start gap-3">
              <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${STATUS_COLORS[t.status]}`}>
                {t.status}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{t.title}</p>
                {t.description && (
                  <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
                )}
                <div className="flex flex-wrap gap-2 items-center mt-1">
                  <span className={`rounded px-2 py-0.5 text-xs ${PRIORITY_COLORS[t.priority]}`}>
                    {t.priority}
                  </span>
                  {t.dueAt && (
                    <span className="text-xs text-muted-foreground">
                      due {new Date(t.dueAt).toLocaleDateString()}
                    </span>
                  )}
                  {regardingLabel(t)}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                {t.status === 'open' && (
                  <>
                    <button
                      onClick={() => completeTask.mutateAsync(t.taskId)}
                      aria-label="Mark complete"
                      title="Mark complete"
                      className="rounded border border-border p-1.5 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => cancelTask.mutateAsync(t.taskId)}
                      aria-label="Cancel task"
                      title="Cancel"
                      className="rounded border border-border p-1.5 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                    >
                      <XCircle className="h-4 w-4" />
                    </button>
                  </>
                )}
                {t.status !== 'open' && (
                  <button
                    onClick={() => reopenTask.mutateAsync(t.taskId)}
                    aria-label="Reopen task"
                    title="Reopen"
                    className="rounded border border-border p-2 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => {
                    if (confirm('Delete this task?')) deleteTask.mutateAsync(t.taskId);
                  }}
                  aria-label="Delete task"
                  title="Delete"
                  className="rounded border border-border p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
        {sortedTasks.length === 0 && !tasksQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">No tasks match.</p>
        )}
      </div>
    </div>
  );
}
